import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

/**
 * What the composer did with the notes it was handed.
 *
 * The composer reads the reply box as two channels: prose meant for the
 * client, and directives meant for the agent ("hey agent, pull their set rate
 * against the vertical"). It executes the directives with real tool calls and
 * folds the answers into the draft. That is useful and also invisible: the
 * staff member gets back a paragraph of numbers with no way to tell whether
 * the agent looked them up or invented them.
 *
 * So this panel shows the work. Which directives it read out of the notes,
 * which tools it actually ran, anything it could not do, and — for every
 * number in the draft — whether the auditor could confirm it and where.
 * Nothing here sends on its own; it exists to be read before a human hits
 * send.
 */

// Tool names are snake_case internals; a chip reading "fetch_vertical_
// benchmark" is noise. Strip the prefix and let the words do the work.
const prettyToolName = (name) =>
  String(name || '')
    .replace(/^fetch_/, '')
    .replace(/_/g, ' ');

const CLAIM_SEVERITY = {
  inaccurate: 'error',
  risky: 'warning',
};

// Where the auditor confirmed a number, in words a staff member recognises.
// Falls back to the free-text `checked_against` the auditor wrote; this is
// the label when it didn't write one.
const SOURCE_LABEL = {
  snapshot: 'matches the data pulled for this draft',
  thread: 'stated in this email thread',
  knowledge: 'from the knowledge base',
  lookup: 'checked against live data',
  arithmetic: 'follows from the budget',
};

export default function ComposerReport({ report, onDismiss }) {
  if (!report) return null;

  const directives = report.directives || [];
  const toolCalls = report.tool_calls || [];
  const notes = (report.notes || '').trim();
  const audit = report.claim_audit;
  const verified = audit?.verified || [];
  const flagged = audit?.claims || [];

  if (!directives.length && !toolCalls.length && !notes && !audit) return null;

  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      {directives.length > 0 && (
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            Read as instructions to the agent (kept out of the email)
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {directives.map((directive) => (
              <Chip key={directive} label={directive} size="small" variant="outlined" />
            ))}
          </Stack>
        </Stack>
      )}

      {toolCalls.length > 0 && (
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            Looked up
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {toolCalls.map((call, idx) => (
              <Chip
                key={`${call.name}-${idx}`}
                label={prettyToolName(call.name)}
                size="small"
                color="info"
                variant="outlined"
              />
            ))}
          </Stack>
        </Stack>
      )}

      {notes && (
        <Alert severity="info" onClose={onDismiss} sx={{ py: 0.5 }}>
          {notes}
        </Alert>
      )}

      {verified.length > 0 && (
        <Alert severity="success" icon={false} sx={{ py: 0.5 }}>
          <AlertTitle sx={{ mb: 0.5 }}>
            {verified.length === 1 ? 'Number checked' : `${verified.length} numbers checked`}
          </AlertTitle>
          <Stack spacing={0.25}>
            {verified.map((claim, idx) => (
              <Box
                key={`${claim.claimed_text}-${idx}`}
                sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}
              >
                <CheckCircleOutlineIcon
                  fontSize="small"
                  color="success"
                  sx={{ mt: '2px', flexShrink: 0 }}
                />
                <Typography variant="body2">
                  {claim.claimed_text}
                  {(claim.checked_against || SOURCE_LABEL[claim.source]) && (
                    <Typography component="span" variant="body2" color="text.secondary">
                      {` — ${claim.checked_against || SOURCE_LABEL[claim.source]}`}
                    </Typography>
                  )}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Alert>
      )}

      {flagged.length > 0 && (
        <Alert severity={CLAIM_SEVERITY[audit.verdict] || 'warning'} sx={{ py: 0.5 }}>
          <AlertTitle sx={{ mb: 0.5 }}>Check these numbers before sending</AlertTitle>
          {audit.summary && (
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {audit.summary}
            </Typography>
          )}
          <Stack component="ul" spacing={0.25} sx={{ pl: 2, m: 0 }}>
            {flagged.map((claim, idx) => (
              <Typography component="li" variant="body2" key={`${claim.claimed_text}-${idx}`}>
                {claim.claimed_text}
                {claim.note ? ` — ${claim.note}` : ''}
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}

const claimShape = PropTypes.shape({
  claimed_text: PropTypes.string,
  checked_against: PropTypes.string,
  source: PropTypes.string,
  note: PropTypes.string,
});

ComposerReport.propTypes = {
  report: PropTypes.shape({
    directives: PropTypes.arrayOf(PropTypes.string),
    tool_calls: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
      }),
    ),
    notes: PropTypes.string,
    claim_audit: PropTypes.shape({
      verdict: PropTypes.string,
      summary: PropTypes.string,
      claims: PropTypes.arrayOf(claimShape),
      verified: PropTypes.arrayOf(claimShape),
      lookups: PropTypes.arrayOf(PropTypes.string),
    }),
  }),
  onDismiss: PropTypes.func,
};

ComposerReport.defaultProps = {
  report: null,
  onDismiss: undefined,
};
